import { AutoComplete } from "primereact/autocomplete";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoWarningOutline } from "react-icons/io5";
import { getLowStockMessage, isLowStockItem } from "../utils/StockAlerts";

function SfdaPriceList({
  searchedItem,
  setSearchedItem,
  handleSelectSearchedItem,
  handleSearchItems,
  loading = false,
  itemsSuggestions,
  disabled = false,
  height = "h-40",
  isFull = false,
  getIsApproval,
}) {
  const { t } = useTranslation();
  const isRtl = document.documentElement.dir === "rtl";
  const [selectedDrugType, setSelectedDrugType] = useState("all");
  const autocompleteWrapperRef = useRef(null);

  const getPanelStyle = useCallback(() => {
    if (!autocompleteWrapperRef.current) return {};

    const width = autocompleteWrapperRef.current.offsetWidth;
    return { width: `${width}px`, maxWidth: `${width}px` };
  }, []);

  const getDrugType = (option) =>
    String(
      option?.item?.drug_type ||
        option?.item?.registration_type ||
        option?.item?.SFDA_Reg_Type ||
        option?.item?.drugtype ||
        "",
    ).trim();

  const drugTypes = useMemo(() => {
    const typeCounts = new Map();

    (Array.isArray(itemsSuggestions) ? itemsSuggestions : []).forEach(
      (option) => {
        const label = getDrugType(option);
        if (!label) return;

        const value = label.toLowerCase();
        const currentType = typeCounts.get(value);
        typeCounts.set(value, {
          value,
          label: currentType?.label || label,
          count: (currentType?.count || 0) + 1,
        });
      },
    );

    return Array.from(typeCounts.values()).sort((first, second) =>
      first.label.localeCompare(second.label),
    );
  }, [itemsSuggestions]);

  useEffect(() => {
    if (
      selectedDrugType !== "all" &&
      !drugTypes.some((drugType) => drugType.value === selectedDrugType)
    ) {
      setSelectedDrugType("all");
    }
  }, [drugTypes, selectedDrugType]);

  const filteredSuggestions = useMemo(() => {
    if (selectedDrugType === "all" || !Array.isArray(itemsSuggestions)) {
      return itemsSuggestions;
    }

    return itemsSuggestions.filter(
      (option) => getDrugType(option).toLowerCase() === selectedDrugType,
    );
  }, [itemsSuggestions, selectedDrugType]);

  const handleComplete = (event) => {
    setSelectedDrugType("all");
    handleSearchItems(event);
  };

  const itemTemplate = (item) => {
    const isApproval = getIsApproval ? getIsApproval(item?.item) : false;
    const itemData = item?.item || {};
    const coverage = String(itemData?.is_covered || "").toLowerCase();
    const coverageLabel = isApproval
      ? t("Need Approval")
      : coverage === "cash"
        ? t("Cash")
        : coverage === "insurance"
          ? t("Covered")
          : null;
    const showLowStockAlert = isLowStockItem(itemData);
    const stockValue =
      itemData?.stock ??
      itemData?.stock_quantity ??
      itemData?.total_stock ??
      itemData?.local_purchase_stock;
    const genericName =
      item?.genericName ||
      itemData?.item_generic_name ||
      itemData?.generic_name ||
      item?.displayName ||
      item?.label;
    const tradeName = itemData?.trade_name || itemData?.name || "";
    const description =
      item?.description ||
      itemData?.second_description ||
      itemData?.pharmaceutical_form ||
      itemData?.administration_route ||
      "";
    const drugType = itemData?.drug_type || itemData?.registration_type;
    const registerNumber = itemData?.register_number || itemData?.item_sfda;
    const code =
      registerNumber ||
      itemData?.gtin ||
      itemData?.item_code ||
      itemData?.item ||
      item?.value ||
      "N/A";

    return (
      <div
        className={`grid w-full grid-cols-[132px_minmax(220px,1.1fr)_minmax(180px,0.9fr)_auto] items-center gap-3 border-b px-3 py-2.5 transition-all cursor-pointer ${isApproval || showLowStockAlert ? "border-rose-100 bg-rose-50/50" : "border-gray-100 bg-white"} hover:bg-slate-50 ${isRtl ? "text-right" : "text-left"}`}
      >
        <div className="min-w-0">
          <span className="inline-flex max-w-full items-center rounded-md bg-blue-50 px-2.5 py-1 text-[12px] font-semibold text-blue-600">
            <span className="truncate">{code}</span>
          </span>
        </div>

        <div className="flex min-w-0 flex-col gap-1">
          {drugType && (
            <span className="w-fit flex-shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold capitalize text-amber-600 ring-1 ring-amber-100">
              {drugType}
            </span>
          )}
          <span className="whitespace-normal break-words text-[13px] font-bold uppercase tracking-normal text-slate-800">
            {tradeName || ""}
          </span>
          <span className="whitespace-normal break-words text-[12px] font-medium text-slate-600">
            {genericName || ""}
          </span>
        </div>

        <div className="min-w-0">
          <span className="line-clamp-1 text-[12.5px] font-medium leading-4 text-slate-600">
            {description || ""}
          </span>
        </div>

        <div className="flex-shrink-0 flex flex-col items-end gap-0.5 whitespace-nowrap">
          {stockValue !== undefined && stockValue !== null && (
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-100">
              {t("Stock")}: {stockValue}
            </span>
          )}
          {showLowStockAlert && (
            <div className="flex items-center gap-1">
              <IoWarningOutline size={14} className="text-amber-600" />
              <span className="text-[11px] font-semibold text-amber-700 leading-none">
                {t(getLowStockMessage(itemData))}
              </span>
            </div>
          )}
          {coverageLabel && (
            <div className="flex items-center gap-1">
              {isApproval && (
                <IoWarningOutline size={14} className="text-rose-600" />
              )}
              <span
                className={`text-[11px] font-semibold leading-none ${isApproval ? "text-black" : coverage === "insurance" ? "text-emerald-700" : "text-slate-700"}`}
              >
                {coverageLabel}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // PrimeReact AutoComplete does not expose a panel header template. Render the
  // controls through its footer slot and move that slot above the scrollable
  // results with the pass-through classes below.
  const panelControlsTemplate = () => (
    <div className="border-b border-gray-200 bg-slate-50">
      {drugTypes.length > 0 && (
        <div
          className={`flex flex-wrap items-center gap-2 border-b border-gray-200 px-3 py-2.5 ${
            isRtl ? "flex-row-reverse justify-end" : ""
          }`}
        >
          {[
            {
              value: "all",
              label: t("All"),
              count: Array.isArray(itemsSuggestions)
                ? itemsSuggestions.length
                : 0,
            },
            ...drugTypes,
          ].map((drugType) => {
            const isActive = selectedDrugType === drugType.value;

            return (
              <button
                key={drugType.value}
                type="button"
                aria-pressed={isActive}
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  event.stopPropagation();
                  setSelectedDrugType(drugType.value);
                }}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold capitalize transition-colors ${
                  isActive
                    ? "bg-primary text-white shadow-sm"
                    : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-100"
                }`}
              >
                <span>{drugType.label}</span>
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] leading-none ${
                    isActive
                      ? "bg-white/20 text-white"
                      : "bg-slate-100 text-slate-500"
                  }`}
                >
                  {drugType.count}
                </span>
              </button>
            );
          })}
        </div>
      )}
      <div
        className={`grid grid-cols-[132px_minmax(220px,1.1fr)_minmax(180px,0.9fr)_auto] gap-3 px-3 py-2 text-[11px] font-bold uppercase tracking-normal text-slate-500 ${isRtl ? "text-right" : "text-left"}`}
      >
        <span>{t("Code")}</span>
        <span>{t("Item Name")}</span>
        <span>{t("Description")}</span>
        <span>{t("Status")}</span>
      </div>
    </div>
  );

  return (
    <div ref={autocompleteWrapperRef} className="w-full">
      <AutoComplete
        disabled={disabled}
        loadingIcon={
          loading ? (
            <i className="pi pi-spin pi-spinner text-primary absolute top-[13px] right-[12px] " />
          ) : (
            <span />
          )
        }
        value={searchedItem}
        field="label"
        suggestions={filteredSuggestions}
        delay={700}
        completeMethod={handleComplete}
        onChange={(e) => {
          setSearchedItem(e?.target?.value);
        }}
        onSelect={handleSelectSearchedItem}
        itemTemplate={itemTemplate}
        panelFooterTemplate={panelControlsTemplate}
        panelStyle={getPanelStyle()}
        forceSelection
        emptyMessage={t("No items found for this search")}
        showEmptyMessage={true}
        placeholder={t("Search SFDA Price List Items")}
        pt={{
          root: { className: "w-full" },
          input: {
            root: {
              className: `input-field !w-full ${height}`,
            },
          },
          panel: {
            className: `!flex !flex-col !overflow-hidden !shadow-lg rounded-[8px] mt-2 !z-[9999999] ${
              isFull ? "!max-h-[70vh] !bg-white" : ""
            }`,
          },
          listWrapper: {
            className: `!order-2 !min-h-0 !flex-1 ${
              isFull ? "!max-h-[70vh]" : ""
            }`,
          },
          footer: { className: "!order-1 !shrink-0 !p-0" },
          list: { className: "!py-0" },
          item: {
            className:
              "!p-0 !text-gray-600 data-[p-highlight='true']:!border-l-[3px] data-[p-highlight='true']:!border-primary rtl:data-[p-highlight='true']:!border-l-0 rtl:data-[p-highlight='true']:!border-r-[3px] rtl:data-[p-highlight='true']:!border-primary",
          },
          loadingIcon: { className: "absolute top-[16px] right-[12px]" },
          emptymesage: { className: "!text-center" },
        }}
      />
    </div>
  );
}

export default SfdaPriceList;
